/**
 * Phase 261j — usePwaNotifications hook.
 *
 * Wraps the browser PushManager + Notification permission lifecycle around the
 * Out-Play backend (/api/push/*). Designed to be mounted from any settings
 * surface — Profile/Security card, mobile drawer, onboarding nudge.
 *
 * Returns:
 *   {
 *     supported: boolean       // browser has SW + PushManager + Notification
 *     permission: 'default' | 'granted' | 'denied' | 'unsupported'
 *     isSubscribed: boolean    // a live subscription exists on this device
 *     status: 'idle' | 'enabling' | 'disabling' | 'testing'
 *     enable: () => Promise<{ ok, reason? }>
 *     disable: () => Promise<{ ok }>
 *     testSelf: () => Promise<{ ok, payload? }>
 *   }
 *
 * Honest semantics — every helper returns an explicit success/reason; never
 * fakes success when the browser denies permission or the SW isn't ready.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { trackPwa, personaForRole } from '@/lib/pwaTelemetry';

function urlBase64ToUint8Array(base64String) {
  // VAPID keys are URL-safe base64 without padding; convert to Uint8Array.
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function detectSupport() {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function usePwaNotifications(user) {
  const [supported] = useState(detectSupport);
  const [permission, setPermission] = useState(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [status, setStatus] = useState('idle');

  // Mirror PushManager state on mount so the UI doesn't lie if the user already
  // enabled push from a prior session.
  useEffect(() => {
    let cancelled = false;
    if (!supported) return undefined;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (!cancelled) setIsSubscribed(!!existing);
      } catch (_) {
        // SW not registered yet — keep isSubscribed false.
      }
    })();

    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) {
      return { ok: false, reason: 'unsupported' };
    }
    setStatus('enabling');
    const persona = personaForRole(user?.role);
    try {
      // 1. Request notification permission (no-op if already granted).
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        trackPwa('pwa_push_denied', { persona, role: user?.role, source: 'profile_card', permission: perm });
        return { ok: false, reason: perm };
      }

      // 2. Wait for SW to be active, fetch VAPID key, then call PushManager.
      const registration = await navigator.serviceWorker.ready;
      const { data: vapidResp } = await api.get('/push/vapid-public-key');
      if (!vapidResp?.public_key) {
        return { ok: false, reason: 'vapid_not_configured' };
      }
      const applicationServerKey = urlBase64ToUint8Array(vapidResp.public_key);

      // Reuse existing subscription if present — saves a round-trip and avoids
      // a fresh PushSubscription when the browser already has one bound.
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // 3. Persist to backend.
      const subJson = subscription.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subJson.endpoint,
        keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
        expirationTime: subJson.expirationTime ?? null,
      });

      setIsSubscribed(true);
      trackPwa('pwa_push_enabled', { persona, role: user?.role, source: 'profile_card' });
      return { ok: true };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[usePwaNotifications] enable failed:', err);
      return { ok: false, reason: err?.message || 'enable_failed' };
    } finally {
      setStatus('idle');
    }
  }, [supported, user?.role]);

  const disable = useCallback(async () => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    setStatus('disabling');
    const persona = personaForRole(user?.role);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Tell backend BEFORE unsubscribing — if the backend call fails we still
        // want the local browser sub removed.
        try {
          await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });
        } catch (_) { /* best-effort */ }
        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
      trackPwa('pwa_push_disabled', { persona, role: user?.role, source: 'profile_card' });
      return { ok: true };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[usePwaNotifications] disable failed:', err);
      return { ok: false, reason: err?.message || 'disable_failed' };
    } finally {
      setStatus('idle');
    }
  }, [supported, user?.role]);

  const testSelf = useCallback(async () => {
    setStatus('testing');
    try {
      const { data } = await api.post('/push/test-self');
      trackPwa('pwa_push_test_fired', {
        persona: personaForRole(user?.role), role: user?.role, source: 'profile_card',
        sent: data?.sent || 0, status: data?.status,
      });
      return { ok: true, payload: data };
    } catch (err) {
      return { ok: false, reason: err?.response?.data?.detail || 'test_failed' };
    } finally {
      setStatus('idle');
    }
  }, [user?.role]);

  return { supported, permission, isSubscribed, status, enable, disable, testSelf };
}
